// 매체 단일 레지스트리 (B-8 Task 1) — App.jsx/useSheets.js 분산 상수 통합.
// 기존 9개 매체의 값(색 hex / 라벨 / 순서)을 그대로 이동 — 값 변경 금지.
// 표기 혼용(source "NAVER_GFA" vs 칩 "NAVER GFA", Google DA/SA 분기, NAVER SA 채널 분해)은
// 필드 분리로 흡수: source(BQ/_media 키) / label / filterValues(필터 칩 문자열, 0~3개) /
// cards(카드 집계 키+색) / cardOrderKeys(카드 노출 순서 키).
// 파생 export(MEDIA_LIST/MEDIA_COLOR_MAP/MEDIA_CARD_COLORS/MEDIA_CARD_ORDER/MEDIA_RAW_CONFIG)는
// 전부 이 레지스트리에서 생성 — 신규 매체는 여기 1곳 또는 loadManualMedia 런타임 병합.
import { NAVER_SA_MEDIA_KEYS } from "./naverSaChannels.js";

export const MEDIA_REGISTRY = [
  {
    source: "META",
    label: "META",
    color: "#0866ff", // §2 잠금값 정렬 (2026-07-20 매체색 SSOT 트랙 Batch 1)
    filterValues: ["META"],
    cards: [{ key: "META", color: "#0866ff" }],
    cardOrderKeys: ["META"],
    rawKey: "metaRaw",
    adsetField: "adset_name",
    active: true,
  },
  {
    source: "Google",
    label: "Google",
    color: "#8a5a33", // 2-D SSOT: google 브라운 (tokens.css --mpx-google 정렬)
    saColor: "#5d3d22", // §2 서브채널: 브라운 hue 다크 스텝 (구 red는 danger 시맨틱 충돌 — 07-20 교정)
    filterValues: ["Google(전체)", "Google SA", "Google DA"],
    cards: [
      { key: "Google DA", color: "#8a5a33" },
      { key: "Google SA", color: "#5d3d22" },
    ],
    cardOrderKeys: ["Google DA", "Google SA"],
    rawKey: "googleRaw",
    adsetField: "adset_name",
    active: true,
  },
  {
    source: "NAVER_GFA",
    label: "NAVER GFA",
    color: "#02823a", // §2 서브채널: NAVER 그린 hue 다크 스텝 (구 amber는 GA4 #e8710a 충돌 — 07-20 교정)
    filterValues: ["NAVER GFA"],
    cards: [{ key: "NAVER_GFA", color: "#02823a" }],
    cardOrderKeys: ["NAVER_GFA"],
    rawKey: "naverGfaRaw",
    adsetField: "adset_name",
    active: true,
  },
  {
    source: "NAVER_SA",
    label: "NAVER SA",
    color: "#03C75A",
    filterValues: ["NAVER SA"],
    // NAVER SA 채널 분해 키 — 기존 단일 색 #03C75A 공유 (새 색 추가 금지)
    cards: [
      { key: "NAVER_SA", color: "#03C75A" },
      ...NAVER_SA_MEDIA_KEYS.map((k) => ({ key: k, color: "#03C75A" })),
    ],
    cardOrderKeys: NAVER_SA_MEDIA_KEYS,
    rawKey: "naverSaRaw",
    adsetField: "adset_name",
    noCreative: true,
    active: true,
  },
  {
    source: "KAKAO",
    label: "KAKAO",
    color: "#fecf00", // §2 잠금값 정렬 (07-20 Batch 1)
    filterValues: ["KAKAO"],
    cards: [{ key: "KAKAO", color: "#fecf00" }],
    cardOrderKeys: ["KAKAO"],
    rawKey: "kakaoRaw",
    adsetField: "adgroup_name",
    active: true,
  },
  {
    source: "TikTok",
    label: "TikTok",
    color: "#000000", // §2 잠금값 정렬 (07-20 Batch 1 — 라이트 전용 제품, 다크 오버라이드는 tokens.css)
    filterValues: ["TikTok"],
    cards: [{ key: "TikTok", color: "#000000" }],
    cardOrderKeys: ["TikTok"],
    rawKey: "tiktokRaw",
    adsetField: "adgroup_name",
    active: true,
  },
  {
    // 필터 칩 없음 (Task 10-C): BQ ad_performance에 source='NAVER' 데이터 0건 — 옵션 노이즈.
    // 색/RAW 설정은 과거 데이터 표시 보호용으로 유지.
    source: "NAVER",
    label: "NAVER",
    color: "#2dd4bf",
    filterValues: [],
    cards: [{ key: "NAVER", color: "#2dd4bf" }],
    cardOrderKeys: ["NAVER"],
    rawKey: "naverRaw",
    adsetField: "adgroup_name",
    active: true,
  },
  {
    source: "Criteo",
    label: "Criteo",
    color: "#ff6e2c", // §2 잠금값 정렬 (07-20 Batch 1)
    filterValues: ["Criteo"],
    cards: [{ key: "Criteo", color: "#ff6e2c" }],
    cardOrderKeys: ["Criteo"],
    rawKey: "criteoRaw",
    adsetField: "adset_name",
    active: true,
  },
  {
    // creative 단위 보조 source — 필터 칩/색/카드 없음 (기존 MEDIA_COLOR_MAP에도 부재)
    source: "NAVER_GFA_CR",
    label: "NAVER_GFA_CR",
    color: null,
    filterValues: [],
    cards: [],
    cardOrderKeys: [],
    rawKey: "naverGfaCrRaw",
    adsetField: "adset_name",
    creativeOnly: true,
    active: true,
  },
];

// ─── 파생 export (기존 분산 상수와 키/값/순서 동일) ───

export const MEDIA_LIST = [
  "전체 매체",
  ...MEDIA_REGISTRY.flatMap((m) => m.filterValues),
];

// 매체 색 팔레트 — 성과분석 행 _mediaColor + B-9 표 컬러 바 공용 (readability Task2 export)
// 키 = data.campaigns _media / campAgg media 표기 (META/Google/NAVER_GFA…)
export const MEDIA_COLOR_MAP = Object.fromEntries(
  MEDIA_REGISTRY.filter((m) => m.color).map((m) => [m.source, m.color]),
);

const GOOGLE_SA_COLOR = MEDIA_REGISTRY.find(
  (m) => m.source === "Google",
).saColor;

export const getMediaColor = (r) => {
  if (r.media === "Google" && r.ad_channel === "SA") return GOOGLE_SA_COLOR; // Google SA 분기
  return MEDIA_COLOR_MAP[r.media] || "#6b7280";
};

// 매체별 성과 요약 카드 색 (카드 키 = Google DA/SA 분기 + NAVER SA 채널 분해 키)
export const MEDIA_CARD_COLORS = Object.fromEntries(
  MEDIA_REGISTRY.flatMap((m) => m.cards.map((c) => [c.key, c.color])),
);

// 카드/테이블 노출 순서 (기존 App.jsx order 배열 2곳 통합)
export const MEDIA_CARD_ORDER = MEDIA_REGISTRY.flatMap((m) => m.cardOrderKeys);

// 매체별 RAW 설정 (필드명 정규화용) — ComparePage 드롭다운이 Object.keys 순서를
// 그대로 쓰므로 기존 useSheets.js 정의 키 순서 고정.
const RAW_CONFIG_KEY_ORDER = [
  "META",
  "Google",
  "KAKAO",
  "NAVER",
  "TikTok",
  "Criteo",
  "NAVER_GFA",
  "NAVER_GFA_CR",
  "NAVER_SA",
];
export const MEDIA_RAW_CONFIG = Object.fromEntries(
  RAW_CONFIG_KEY_ORDER.map((src) => {
    const m = MEDIA_REGISTRY.find((e) => e.source === src);
    const cfg = { rawKey: m.rawKey, adsetField: m.adsetField };
    if (m.creativeOnly) cfg.creativeOnly = true;
    if (m.noCreative) cfg.noCreative = true;
    return [src, cfg];
  }),
);

// 소재(creatives.source) ↔ 매체 필터 칩 매칭 — 기존 App.jsx matchSrc 인라인 이동 (값 동일)
const FILTER_TO_CREATIVE_SOURCES = {
  "Google(전체)": ["GOOGLE", "Google"],
  "Google SA": ["GOOGLE", "Google"],
  "Google DA": ["GOOGLE", "Google"],
  "NAVER GFA": ["NAVER_GFA"],
  // 18-24 Phase 2-B: KAKAO 매체 필터 — creatives.source = "KAKAO_MOMENT" 사양 정합
  KAKAO: ["KAKAO_MOMENT"],
  // 18-25: Criteo 매체 필터 — ad_performance.source = "CRITEO" 사양 정합
  Criteo: ["CRITEO"],
};
export const matchCreativeSource = (src, mediaFilter) => {
  const list = FILTER_TO_CREATIVE_SOURCES[mediaFilter];
  if (list) return list.includes(src);
  return src === mediaFilter || src === mediaFilter.toUpperCase();
};

// ─── 수동 매체 런타임 병합 (B-8) ───
// active:true → 필터 칩·카드 순서에 노출. active 무관 → 색/라벨 조회는 응답
// (과거 데이터·과거 리포트 표시 보호 — spec 확정 결정 4·Task 1-6).
// Task 8: _media 값 = label 단일 소스 — 카드/색/필터/RAW 키 전부 label 기준으로 통일.
// MEDIA_RAW_CONFIG[label] 동적 등록 → App.jsx 기존 4개 루프가 data.raw[source]를
// _media=label로 자동 합류 (신규 하드코딩 없음).
// Task 9: 브랜드 귀속(B안) — brandSlug 일치 엔트리만 병합 + 교체 방식
// (호출 시 기존 manual 병합분 전체 제거 후 재병합 — 브랜드 전환 잔존 차단).

// 기존 9매체(빌트인) 키 스냅샷 — 수동 매체 label 충돌 시 빌트인 보호
const BUILTIN_LIST = new Set(MEDIA_LIST);
const BUILTIN_RAW_KEYS = new Set(Object.keys(MEDIA_RAW_CONFIG));

function removeManualEntries() {
  for (const e of MEDIA_REGISTRY.filter((m) => m.manual)) {
    const li = MEDIA_LIST.indexOf(e.label);
    if (li >= 0) MEDIA_LIST.splice(li, 1);
    const oi = MEDIA_CARD_ORDER.indexOf(e.label);
    if (oi >= 0) MEDIA_CARD_ORDER.splice(oi, 1);
    delete MEDIA_RAW_CONFIG[e.label];
    delete FILTER_TO_CREATIVE_SOURCES[e.label];
    delete MEDIA_COLOR_MAP[e.label];
    delete MEDIA_COLOR_MAP[e.source];
    delete MEDIA_CARD_COLORS[e.label];
    delete MEDIA_CARD_COLORS[e.source];
  }
  for (let i = MEDIA_REGISTRY.length - 1; i >= 0; i--) {
    if (MEDIA_REGISTRY[i].manual) MEDIA_REGISTRY.splice(i, 1);
  }
}

export function loadManualMedia(config, brandSlug = "") {
  removeManualEntries(); // 교체 방식 — 이전 브랜드 병합분 제거
  const media = config?.media;
  if (!Array.isArray(media) || !brandSlug) return;
  media.forEach((m) => {
    if (!m?.source || m.brand_id !== brandSlug) return; // Task 9: 브랜드 귀속
    if (MEDIA_REGISTRY.some((e) => e.source === m.source)) return;
    const label = m.label || m.source;
    // 빌트인 키와 충돌하는 라벨은 병합 거부 — 기존 9매체 보호
    if (BUILTIN_LIST.has(label) || BUILTIN_RAW_KEYS.has(label)) return;
    const color = m.color || "#6b7280";
    const entry = {
      source: m.source,
      label,
      color,
      filterValues: m.active ? [label] : [],
      cards: [{ key: label, color }],
      cardOrderKeys: m.active ? [label] : [],
      rawKey: m.source, // useSheets data.raw[source] (Task 8 연결)
      adsetField: "adset_name",
      manual: true,
      active: !!m.active,
    };
    MEDIA_REGISTRY.push(entry);
    // 색 조회는 label(_media/카드 키) 기준 + source 방어 등록 (과거 데이터 표기 혼용 대비)
    MEDIA_COLOR_MAP[label] = color;
    MEDIA_COLOR_MAP[entry.source] = color;
    MEDIA_CARD_COLORS[label] = color;
    MEDIA_CARD_COLORS[entry.source] = color;
    // RAW 경로: 비활성 매체도 등록 — 과거 데이터 표시 유지 (필터 칩만 숨김, 결정 4)
    MEDIA_RAW_CONFIG[label] = {
      rawKey: entry.rawKey,
      adsetField: entry.adsetField,
    };
    // 소재 필터: 칩(label) ↔ creatives.source(MANUAL_X) 매칭
    FILTER_TO_CREATIVE_SOURCES[label] = [entry.source];
    if (entry.active) {
      MEDIA_LIST.push(label);
      MEDIA_CARD_ORDER.push(label);
    }
  });
}

// manual_media/config.json fetch — Task 8 (진단 후보 B): R2 공개 URL 직접
// (data.json과 동일 의존 — 인증 불요, 부트 401 차단 해소). 캐시 우회 no-store.
// 실패/404/env 부재 시 기존 9개 매체로 정상 동작 (silent 폴백).
// promise 캐시: 모듈 스코프 1회 + useSheets fetchData await가 같은 호출 공유.
// import.meta.env?. — Vite 빌드에선 치환, node(골든 테스트)에선 undefined 안전
const R2_PUBLIC_URL = (import.meta.env?.VITE_R2_PUBLIC_URL || "").replace(
  /\/+$/,
  "",
);
let _configPromise = null;
export function fetchManualMediaConfig() {
  if (_configPromise) return _configPromise;
  _configPromise = (async () => {
    if (!R2_PUBLIC_URL) return null;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      const res = await fetch(`${R2_PUBLIC_URL}/manual_media/config.json`, {
        cache: "no-store",
        signal: controller.signal,
      });
      if (!res.ok) {
        _configPromise = null;
        return null;
      }
      return await res.json();
    } catch {
      // A stalled optional registry must not trap every brand's dashboard boot.
      // Let the next explicit fetch retry instead of caching failure forever.
      _configPromise = null;
      return null;
    } finally {
      clearTimeout(timeout);
    }
  })();
  return _configPromise;
}
fetchManualMediaConfig();
